import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import PdfUploader from "@/components/PdfUploader";
import { atsAPI, exportAPI } from "@/lib/api";
import { Target, Upload, Loader2, CheckCircle, XCircle, FileDown, Lightbulb, HelpCircle, AlertTriangle, PenLine, Search, Tag, ArrowRight, FileText, BarChart3, ShieldCheck, ClipboardList, Copy } from "lucide-react";
import { toast } from "sonner";

const strengthConfig = {
  strong: { color: "bg-[#007F3B]", badge: "bg-[#007F3B]/10 text-[#007F3B]", label: "Strong" },
  partial: { color: "bg-[#FFB81C]", badge: "bg-[#FFB81C]/10 text-[#996B00]", label: "Partial" },
  gap: { color: "bg-[#D5281B]", badge: "bg-[#D5281B]/10 text-[#D5281B]", label: "Gap" },
};

function EvidenceMapItem({ item }) {
  const cfg = strengthConfig[item.strength] || strengthConfig.gap;
  const bullets = item.evidence_bullets || [];

  const handleCopy = () => {
    const text = bullets.length > 0
      ? `${item.criterion}\n${bullets.map(b => `  - ${b}`).join("\n")}`
      : item.criterion;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`evidence-map-item-${item.strength}`}>
      <div className="flex items-start gap-2">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${cfg.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-900">{item.criterion}</span>
            <Badge className={cfg.badge} variant="outline">{cfg.label}</Badge>
          </div>
          {bullets.length > 0 ? (
            <ul className="mt-1.5 space-y-1">
              {bullets.map((b, i) => (
                <li key={i} className="text-sm text-slate-600 flex items-start gap-1.5">
                  <span className="text-slate-400 mt-0.5 flex-shrink-0">-</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400 mt-1 italic">
              {item.evidence_found || "No evidence found in your document"}
            </p>
          )}
        </div>
        {bullets.length > 0 && (
          <button onClick={handleCopy} className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0" title="Copy evidence">
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ATSAnalyzer() {
  const navigate = useNavigate();
  const [cvText, setCvText] = useState("");
  const [jdText, setJdText] = useState("");
  const [psText, setPsText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    if (!cvText.trim() || !jdText.trim()) {
      toast.error("Please provide both your document and the job description");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await atsAPI.analyze({ cv_text: cvText, jd_text: jdText, ps_text: psText });
      setResult(res.data);
      toast.success("Analysis complete!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!result) return;
    try {
      const improvementLines = (result.areas_of_improvement || []).map((item, i) =>
        `### ${i + 1}. ${item.requirement}\n- Gap: ${item.current_gap}\n- Evidence to add: ${item.evidence_to_add}\n- Keywords: ${(item.keywords_to_include || []).join(", ")}\n- Suggestion: ${item.rewrite_suggestion}`
      ).join("\n\n");
      const psCoverageLines = result.ps_coverage ? [
        `## Person Specification Coverage`,
        `Essential Criteria: ${result.ps_coverage.essential_coverage_pct}%`,
        `Desirable Criteria: ${result.ps_coverage.desirable_coverage_pct}%`,
        `\n## Shortlisting Simulation`,
        `Panel Score: ${result.ps_coverage.panel_score} / ${result.ps_coverage.panel_max}`,
        `Likely Outcome: ${result.ps_coverage.likely_outcome}`,
        result.ps_coverage.missing_evidence?.length > 0 ? `\n## Missing Evidence\n${result.ps_coverage.missing_evidence.map(m => `- ${m}`).join("\n")}` : "",
      ].filter(Boolean).join("\n") : "";
      const evidenceMapLines = result.evidence_map?.items?.length > 0 ? [
        `## Person Specification Evidence Map`,
        result.evidence_map.summary || "",
        `\n### Essential Criteria`,
        ...result.evidence_map.items.filter(i => i.type === "essential").map(i => {
          const bullets = (i.evidence_bullets || []).map(b => `  - ${b}`).join("\n");
          return `- ${i.criterion} [${(i.strength || "").toUpperCase()}]\n${bullets || "  No evidence found"}`;
        }),
        `\n### Desirable Criteria`,
        ...result.evidence_map.items.filter(i => i.type === "desirable").map(i => {
          const bullets = (i.evidence_bullets || []).map(b => `  - ${b}`).join("\n");
          return `- ${i.criterion} [${(i.strength || "").toUpperCase()}]\n${bullets || "  No evidence found"}`;
        }),
      ].filter(Boolean).join("\n") : "";
      const content = [
        `# Application Analysis Report`,
        `## Match Score: ${result.score}/100`,
        `## Summary\n${result.summary}`,
        `## Strengths\n${(result.strengths || []).map(s => `- ${s}`).join("\n")}`,
        `## Missing Skills\n${(result.missing_skills || []).map(s => `- ${s}`).join("\n")}`,
        `## Areas of Improvement\n${improvementLines}`,
        psCoverageLines,
        evidenceMapLines,
        `## Interview Questions\n${(result.interview_questions || []).map((q, i) => `${i + 1}. ${q}`).join("\n")}`,
        `## Recommendations\n${(result.recommendations || []).map(r => `- ${r}`).join("\n")}`,
      ].filter(Boolean).join("\n\n");
      const res = await exportAPI.docx({ content, title: "Application Analysis Report" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Application_Analysis_Report.docx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Document downloaded!");
    } catch {
      toast.error("Export failed");
    }
  };

  const scoreColor = (s) => s >= 70 ? "#007F3B" : s >= 40 ? "#FFB81C" : "#D5281B";
  const ps = result?.ps_coverage;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="ats-page-title">Application Analyzer</h1>
          <p className="text-sm text-slate-500 mt-1">Analyze your CV or Supporting Information against the Job Description and Person Specification</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Column */}
          <div className="space-y-4">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Your Document (CV or Supporting Information)</CardTitle>
                <CardDescription className="text-xs">Paste your CV or supporting information, or upload a PDF/DOCX</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <PdfUploader onTextExtracted={(text) => setCvText(text)} label="Upload CV (PDF or DOCX)" />
                <Textarea placeholder="Paste your CV or Supporting Information here..." className="min-h-[140px] text-sm" value={cvText} onChange={e => setCvText(e.target.value)} data-testid="ats-cv-input" />
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Job Description</CardTitle>
                <CardDescription className="text-xs">Paste the job description or upload a PDF/DOCX</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <PdfUploader onTextExtracted={(text) => setJdText(text)} label="Upload JD (PDF or DOCX)" />
                <Textarea placeholder="Paste the full job description..." className="min-h-[100px] text-sm" value={jdText} onChange={e => setJdText(e.target.value)} data-testid="ats-jd-input" />
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Person Specification <Badge variant="outline" className="text-xs ml-2">Recommended</Badge></CardTitle>
                <CardDescription className="text-xs">Adding the Person Specification enables PS coverage analysis and shortlisting simulation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <PdfUploader onTextExtracted={(text) => setPsText(text)} label="Upload PS (PDF or DOCX)" />
                <Textarea placeholder="Paste the Person Specification here..." className="min-h-[100px] text-sm" value={psText} onChange={e => setPsText(e.target.value)} data-testid="ats-ps-input" />
              </CardContent>
            </Card>

            <Button className="w-full bg-[#003087] text-white hover:bg-[#002366]" onClick={handleAnalyze} disabled={loading} data-testid="ats-analyze-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <><Target className="w-4 h-4 mr-2" /> Analyze Application</>}
            </Button>
          </div>

          {/* Results Column */}
          <div className="space-y-4">
            {!result && !loading && (
              <Card className="bg-slate-50 border-dashed border-2 border-slate-200 rounded-xl">
                <CardContent className="py-12 text-center text-slate-400">
                  <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Upload your document and job description to begin analysis</p>
                </CardContent>
              </Card>
            )}
            {loading && (
              <Card className="bg-white border border-slate-200 rounded-xl">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#003087]" />
                  <p className="text-sm text-slate-600">Analyzing your application...</p>
                </CardContent>
              </Card>
            )}

            {result && (
              <>
                {/* Match Score */}
                <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="ats-score-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-slate-500">Application Match Score</p>
                        <p className="text-3xl font-bold" style={{ color: scoreColor(result.score) }}>{result.score}%</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleExport} data-testid="ats-export-btn">
                        <FileDown className="w-4 h-4 mr-1" /> Export
                      </Button>
                    </div>
                    <Progress value={result.score} className="h-2" />
                    <p className="text-sm text-slate-600 mt-3">{result.summary}</p>
                    {ps && ps.essential_coverage_pct < 50 && result.score >= 60 && (
                      <p className="text-xs text-[#9A6700] mt-2 bg-[#FFB81C]/10 p-2 rounded" data-testid="ats-score-context-note">
                        Note: This score reflects broad CV/JD keyword relevance. Your Person Specification coverage is low ({ps.essential_coverage_pct}% essential), which means the application may not score well at shortlisting despite general relevance to the role.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* PS Coverage + Shortlisting */}
                {ps && (
                  <>
                    <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="ats-ps-coverage-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#003087]" /> Person Specification Coverage</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Essential Criteria</p>
                            <p className="text-2xl font-bold" style={{ color: scoreColor(ps.essential_coverage_pct) }}>{ps.essential_coverage_pct}%</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Desirable Criteria</p>
                            <p className="text-2xl font-bold" style={{ color: scoreColor(ps.desirable_coverage_pct) }}>{ps.desirable_coverage_pct}%</p>
                          </div>
                        </div>
                        {ps.criteria_results?.length > 0 && (
                          <div className="space-y-1.5 pt-1">
                            {ps.criteria_results.map((c, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${c.score >= 3 ? "bg-[#007F3B]" : c.score >= 2 ? "bg-[#FFB81C]" : c.score >= 1 ? "bg-orange-400" : "bg-[#D5281B]"}`} />
                                <span className="flex-1 text-slate-700 truncate">{c.criterion}</span>
                                <Badge variant="outline" className={`text-[10px] ${c.type === "essential" ? "border-blue-200 text-blue-700" : "border-slate-200 text-slate-500"}`}>{c.type}</Badge>
                                <span className="text-slate-400 w-8 text-right">{c.score}/3</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="ats-shortlisting-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#00A3A6]" /> Shortlisting Simulation</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-slate-500 font-medium">Estimated Panel Score</p>
                            <p className="text-2xl font-bold text-slate-900">{ps.panel_score} / {ps.panel_max}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500 font-medium">Likely Outcome</p>
                            <p className={`text-sm font-semibold ${ps.essential_coverage_pct >= 80 ? "text-[#007F3B]" : ps.essential_coverage_pct >= 50 ? "text-[#9A6700]" : "text-[#D5281B]"}`}>
                              {ps.likely_outcome}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Missing Evidence */}
                    {(ps.missing_evidence?.length > 0 || ps.weak_evidence?.length > 0) && (
                      <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="ats-missing-evidence-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#D5281B]" /> Missing & Weak Evidence</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {ps.missing_evidence?.map((m, i) => (
                            <div key={`m-${i}`} className="flex items-start gap-2 text-sm">
                              <XCircle className="w-3.5 h-3.5 text-[#D5281B] mt-0.5 flex-shrink-0" />
                              <span className="text-slate-700">{m}</span>
                            </div>
                          ))}
                          {ps.weak_evidence?.map((w, i) => (
                            <div key={`w-${i}`} className="flex items-start gap-2 text-sm">
                              <AlertTriangle className="w-3.5 h-3.5 text-[#FFB81C] mt-0.5 flex-shrink-0" />
                              <span className="text-slate-700">{w}</span>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}

                {/* Evidence Map */}
                {result.evidence_map && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="ats-evidence-map-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#00A3A6]" /> Person Specification Evidence Map
                      </CardTitle>
                      {result.evidence_map.summary && (
                        <p className="text-sm text-slate-600 mt-1" data-testid="ats-evidence-map-summary">{result.evidence_map.summary}</p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {/* Essential Criteria */}
                      {result.evidence_map.items?.filter(i => i.type === "essential").length > 0 && (
                        <div className="space-y-3">
                          <p className="text-xs font-semibold text-[#003087] uppercase tracking-wide pt-1">Essential Criteria</p>
                          {result.evidence_map.items.filter(i => i.type === "essential").map((item, idx) => (
                            <EvidenceMapItem key={`ess-${idx}`} item={item} />
                          ))}
                        </div>
                      )}
                      {/* Desirable Criteria */}
                      {result.evidence_map.items?.filter(i => i.type === "desirable").length > 0 && (
                        <div className="space-y-3 pt-3">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Desirable Criteria</p>
                          {result.evidence_map.items.filter(i => i.type === "desirable").map((item, idx) => (
                            <EvidenceMapItem key={`des-${idx}`} item={item} />
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Strengths + Missing Skills */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {result.strengths?.length > 0 && (
                    <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#007F3B]" /> Strengths</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1.5">
                          {result.strengths.map((s, i) => (
                            <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                              <CheckCircle className="w-3.5 h-3.5 text-[#007F3B] mt-0.5 flex-shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                  {result.missing_skills?.length > 0 && (
                    <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><XCircle className="w-4 h-4 text-[#D5281B]" /> Missing Skills</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1.5">
                          {result.missing_skills.map((s, i) => (
                            <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                              <XCircle className="w-3.5 h-3.5 text-[#D5281B] mt-0.5 flex-shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Areas of Improvement */}
                {result.areas_of_improvement?.length > 0 && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="ats-areas-of-improvement">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[#FFB81C]" /> Areas of Improvement</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {result.areas_of_improvement.map((item, i) => (
                        <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 space-y-3" data-testid={`improvement-item-${i}`}>
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-[#FFB81C] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{item.requirement}</p>
                              <p className="text-sm text-slate-600 mt-1">{item.current_gap}</p>
                            </div>
                          </div>
                          <Separator />
                          <div className="flex items-start gap-2">
                            <Search className="w-4 h-4 text-[#007F3B] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Evidence to Add</p>
                              <p className="text-sm text-slate-700 mt-0.5">{item.evidence_to_add}</p>
                            </div>
                          </div>
                          {item.keywords_to_include?.length > 0 && (
                            <div className="flex items-start gap-2">
                              <Tag className="w-4 h-4 text-[#003087] mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Keywords to Include</p>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {item.keywords_to_include.map((kw, ki) => (
                                    <Badge key={ki} variant="outline" className="border-blue-200 text-blue-700 bg-blue-50 text-xs">{kw}</Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="flex items-start gap-2">
                            <PenLine className="w-4 h-4 text-[#00A3A6] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Rewrite Suggestion</p>
                              <p className="text-sm text-slate-700 mt-0.5 italic bg-white p-2 rounded border border-slate-100">{item.rewrite_suggestion}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Interview Questions */}
                {result.interview_questions?.length > 0 && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2"><HelpCircle className="w-4 h-4 text-[#00A3A6]" /> Interview Questions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ol className="space-y-2 list-decimal list-inside">
                        {result.interview_questions.map((q, i) => (
                          <li key={i} className="text-sm text-slate-700">{q}</li>
                        ))}
                      </ol>
                    </CardContent>
                  </Card>
                )}

                {/* Outcome-Based Recommendation */}
                {result.score != null && (() => {
                  // Determine effective strength using PS coverage when available
                  const psEssential = ps?.essential_coverage_pct ?? null;
                  const isStrongApplication = psEssential !== null
                    ? (result.score >= 70 && psEssential >= 60)
                    : (result.score >= 70);
                  const isWeakApplication = psEssential !== null
                    ? (result.score < 40 || psEssential < 30)
                    : (result.score < 40);
                  // Middle ground
                  const isMediumApplication = !isStrongApplication && !isWeakApplication;

                  return (
                  <Card
                    className={`border shadow-sm rounded-xl ${
                      isStrongApplication
                        ? "bg-[#007F3B]/5 border-[#007F3B]/20"
                        : isWeakApplication
                        ? "bg-[#D5281B]/5 border-[#D5281B]/20"
                        : "bg-[#FFB81C]/5 border-[#FFB81C]/30"
                    }`}
                    data-testid="ats-recommendation-card"
                  >
                    <CardContent className="p-5 space-y-3">
                      {isStrongApplication ? (
                        <>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-[#007F3B] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-slate-900">Your application appears strong and well aligned to the role requirements.</p>
                              <p className="text-sm text-slate-600 mt-1">The next best step is to prepare for interview using the Interview Simulator, which will generate panel-style questions based on the role and assess your readiness.</p>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <Button size="sm" className="bg-[#003087] text-white hover:bg-[#002366]" onClick={() => navigate("/interview")} data-testid="ats-rec-interview-btn">
                              Try Interview Simulator <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                          </div>
                        </>
                      ) : isMediumApplication ? (
                        <>
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-[#9A6700] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-slate-900">Your application needs strengthening before interview preparation.</p>
                              <p className="text-sm text-slate-600 mt-1">Use the SI Generator to produce a stronger Supporting Information aligned to the Person Specification, and the CV Rewriter to better match your CV to the Job Description.</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 pt-1">
                            <Button size="sm" className="bg-[#003087] text-white hover:bg-[#002366]" onClick={() => navigate("/nhs-generator")} data-testid="ats-rec-si-btn">
                              <FileText className="w-3.5 h-3.5 mr-1" /> Improve with SI Generator
                            </Button>
                            <Button size="sm" variant="outline" className="border-[#003087] text-[#003087] hover:bg-[#003087]/5" onClick={() => navigate("/cv-rewriter")} data-testid="ats-rec-cv-btn">
                              <PenLine className="w-3.5 h-3.5 mr-1" /> Rewrite my CV
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-5 h-5 text-[#D5281B] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-slate-900">Your application needs significant improvement before progressing to interview stage.</p>
                              <p className="text-sm text-slate-600 mt-1">Use the SI Generator to create a stronger, criteria-aligned Supporting Information and the CV Rewriter to restructure your CV against the Job Description and Person Specification.</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 pt-1">
                            <Button size="sm" className="bg-[#003087] text-white hover:bg-[#002366]" onClick={() => navigate("/nhs-generator")} data-testid="ats-rec-si-btn">
                              <FileText className="w-3.5 h-3.5 mr-1" /> Improve with SI Generator
                            </Button>
                            <Button size="sm" variant="outline" className="border-[#003087] text-[#003087] hover:bg-[#003087]/5" onClick={() => navigate("/cv-rewriter")} data-testid="ats-rec-cv-btn">
                              <PenLine className="w-3.5 h-3.5 mr-1" /> Rewrite my CV
                            </Button>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
